/* eslint-disable react/jsx-max-depth */
import { parse, format } from "date-fns";
import * as React from "react";
import { generate } from "shortid";
import { PageTitle } from "@plinknz/tah-website-elements";
import { Link, Route, Switch, useHistory, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { byPrefixAndName } from "@awesome.me/kit-d560caf08c/icons/modules";

import * as ROUTES from "../../../../config/router";
import { Loader } from "../../../../components/loader";
import { Hero } from "../../../../components/hero";
import { PAGE_SUFFIX } from "../../../page";
import { UserContext } from "../../../../service/reducers/user";
import { fetchUser } from "../../../../utility/load-member";
import { getPhone, PhoneType } from "../../../../utility/phone";
import { UpdateDetails } from "./update-details.component";

export const DetailsComponent = () => {
  const {
    state: { user, member },
    dispatch,
  } = React.useContext(UserContext);
  const [isLoading, setLoading] = React.useState<boolean>(false);
  const history = useHistory();
  const location = useLocation();

  async function populateUser() {
    setLoading(true);
    await fetchUser({
      dispatch,
      history,
      location,
      id: user.id,
    });
  }

  React.useEffect(() => {
    if (user?.id && !member) {
      populateUser();
    }
  }, [user?.id]);

  React.useEffect(() => {
    if (member) {
      setLoading(false);
    }
  }, [member]);

  const renderLine = (v: string | string[]) => {
    if (!v) {
      return null;
    }

    if (Array.isArray(v)) {
      return (
        <React.Fragment key={generate()}>{v.map(renderLine)}</React.Fragment>
      );
    }

    return <p key={generate()}>{v}</p>;
  };
  type ColumnValue =
    | (string | null)
    | (string | string[] | undefined)
    | (string | string[][] | undefined);

  const renderRow = (columns: ColumnValue[]) => (
    <tr key={generate()} className="table-row">
      {columns.map((value: ColumnValue) => (
        <td className="table-cell" key={generate()}>
          {(Array.isArray(value) &&
            ((value as unknown) as string[]).map(renderLine)) ||
            value ||
            "-"}
        </td>
      ))}
    </tr>
  );

  const renderTable = (rows: ColumnValue[][]) => (
    <table className="table">
      <tbody>{rows.map(renderRow)}</tbody>
    </table>
  );

  let addressDisplay: string[];
  let phoneDisplay: string;
  let emailDisplay: string;
  let cellDisplay: string;
  let rows: ColumnValue[][];

  if (member) {
    const {
      title,
      firstName,
      middleNames,
      surname,
      aliases,
      email,
      emailSurrogated,
      address,
      addressSurrogated,
      phone,
      phoneSurrogated,
      gender,
      dob,
      occupation,
    } = member;

    if (addressSurrogated?.length) {
      const {
        line1,
        line2,
        suburb,
        city,
        postcode,
        state,
        country,
      } = addressSurrogated[0];
      addressDisplay = [
        line1,
        line2,
        suburb,
        `${city} ${postcode}`,
        state,
        country,
      ];
    } else if (address?.length) {
      const {
        line1,
        line2,
        suburb,
        city,
        postcode,
        state,
        country,
      } = address[0];
      addressDisplay = [
        line1,
        line2,
        suburb,
        `${city} ${postcode}`,
        state,
        country,
      ];
    }

    if (emailSurrogated?.length) {
      emailDisplay = emailSurrogated[0].value;
    } else if (email?.length) {
      emailDisplay = email[0].value;
    }

    if (phoneSurrogated?.length) {
      phoneDisplay = getPhone({ phone: phoneSurrogated, type: PhoneType.Home })
        ?.value;
      cellDisplay = getPhone({ phone: phoneSurrogated, type: PhoneType.Cell })
        ?.value;
    }

    if (phone?.length) {
      if (!phoneDisplay) {
        phoneDisplay = getPhone({ phone, type: PhoneType.Home })?.value;
      }

      if (!cellDisplay) {
        cellDisplay = getPhone({ phone, type: PhoneType.Cell })?.value;
      }
    }

    rows = [
      ["Title", title],
      ["First Name", firstName],
      ["Middle names", middleNames],
      ["Surname", surname],
      ["Aliases", aliases],
      ["Email", emailDisplay],
      ["Address", addressDisplay],
      ["Phone", phoneDisplay],
      ["Cell", cellDisplay],
      ["Gender", gender],
      [
        "Date of Birth",
        dob ? format(parse(dob, "yyyymmdd", new Date()), "dd/mm/yyyy") : null,
      ],
      ["Occupation", occupation],
    ];
  }

  // Exclude font awesome icons in testing, otherwise debugging is pretty much impossible
  const isTest = user?.name === "__WHAKAMATAUTAU__";

  return (
    <div data-testid="moku-details">
      <PageTitle title="Mōku" suffix={PAGE_SUFFIX} />
      <Hero title="My Details" subtitle="Mōku" />
      <div className="constrain-width">
        <Switch>
          <Route path={ROUTES.membersDetailsEdit()}>
            <UpdateDetails />
          </Route>
          <Route path={ROUTES.membersDetails()}>
            {isLoading && (
              <div className="splash">
                <Loader size="large" />
              </div>
            )}
            {member && (
              <div data-testid="member-details">
                {renderTable(rows)}
                <Link
                  to={ROUTES.membersDetailsEdit()}
                  className="button secondary"
                  type="button"
                >
                  Edit my information{" "}
                  {!isTest && (
                    <FontAwesomeIcon
                      icon={byPrefixAndName.fas["pencil-alt"]}
                      className="button-icon"
                    />
                  )}
                </Link>
              </div>
            )}
          </Route>
        </Switch>
      </div>
    </div>
  );
};
